import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { toast } from 'sonner'
import type { GroupBillingMode, GroupSummary as GroupSummaryData } from '@shared/types'
import {
  apiError,
  isNotFound,
  useGroupStatementQuery,
  useGroupStatementsQuery,
  useGroupSummaryQuery,
  useMeQuery,
  useSetGroupBillingMutation,
} from '@/store/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, THead, Th, Tr, Td, SkeletonRows, TotalsRow } from '@/components/ui/data-table'
import { ErrorState } from '@/components/ui/empty-state'
import { recentPeriods } from '@/components/ui/period-select'
import { Term } from '@/components/ui/term'
import { NotFound } from '@/pages/NotFound'
import { fmtDate, fmtMoney, fmtNum, fmtPeriod } from '@/lib/format'

/** Every brand in a hosting group for the current Billing Period, and how Atarim invoices the group. */
export function GroupSummary() {
  const { groupSlug = '' } = useParams()
  const { data, isLoading, isError, error, refetch } = useGroupSummaryQuery(groupSlug)

  if (isError && isNotFound(error)) return <NotFound />

  return (
    <>
      <PageHeader
        title={data?.group.name ?? 'Group summary'}
        description={
          data ? (
            <>
              {fmtPeriod(data.period)}. Each brand keeps its own <Term id="licence-entitlement">Licence Entitlements</Term>, tier,
              prices and statement — they don't pool.{' '}
              {data.group.billingMode === 'group'
                ? `Atarim invoices ${data.group.billing.entity || data.group.name} once for all brands.`
                : 'Atarim invoices each brand separately.'}
            </>
          ) : undefined
        }
      />
      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : (
        <Table>
          <THead>
            <Th>Brand</Th>
            <Th right>Licence Entitlements</Th>
            <Th right>Licences granted</Th>
            <Th right>Undrawn</Th>
            <Th right>SMB owners free / paid</Th>
            <Th right>Licence Fees</Th>
            <Th right>Expansion Charges (provisional)</Th>
          </THead>
          {isLoading ? (
            <SkeletonRows cols={7} rows={3} />
          ) : (
            <tbody>
              {data!.brands.map((b) => (
                <Tr key={b.partner.id}>
                  <Td className="font-medium">
                    <Link to={`/p/${b.partner.slug}/overview`} className="hover:underline">
                      {b.partner.name}
                    </Link>
                    {b.suspended && (
                      <span className="ml-2">
                        <Badge variant="revoked">Suspended</Badge>
                      </span>
                    )}
                  </Td>
                  <Td right>
                    {fmtNum(b.entitlements.held)}
                    <span className="block text-xs text-muted-foreground">{fmtMoney(b.entitlements.rateCents, 'USD')} each</span>
                  </Td>
                  <Td right>{fmtNum(b.entitlements.granted)}</Td>
                  <Td right>{fmtNum(b.entitlements.undrawn)}</Td>
                  <Td right>
                    {fmtNum(b.smbFree)} / {fmtNum(b.smbPaid)}
                  </Td>
                  <Td right>{fmtMoney(b.licenceFeesCents, 'USD')}</Td>
                  <Td right>{fmtMoney(b.expansionChargesMinor, b.currency)}</Td>
                </Tr>
              ))}
            </tbody>
          )}
        </Table>
      )}
      {data?.group.billingMode === 'group' && <GroupStatements slug={groupSlug} />}
      {data && <GroupBillingPanel group={data.group} />}
    </>
  )
}

/** The single invoice for a group billed as one: every brand's statement, added up in USD. */
function GroupStatements({ slug }: { slug: string }) {
  const { data: closed } = useGroupStatementsQuery(slug)
  const current = recentPeriods(1)[0]
  const [period, setPeriod] = useState<string | null>(null)
  const selected = period ?? closed?.[0]?.period ?? current
  const { data, isLoading, isError, error, refetch } = useGroupStatementQuery({ slug, period: selected })
  const periods = new Set([current, ...(closed ?? []).map((c) => c.period)])
  const base = `/api/groups/${slug}/statements/${selected}`

  return (
    <section className="mt-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Group statement</h2>
          <p className="text-sm text-muted-foreground">Final once every brand's Billing Period is closed. Payment is due 14 days after the statement date.</p>
        </div>
        <Select
          ariaLabel="Group statement"
          value={selected}
          onValueChange={setPeriod}
          options={[...periods].sort().reverse().map((p) => {
            const c = closed?.find((x) => x.period === p)
            return { value: p, label: `${fmtPeriod(p)}${c?.complete ? '' : ' — provisional'}` }
          })}
        />
      </div>
      {isError ? (
        <ErrorState message={apiError(error)} onRetry={refetch} />
      ) : isLoading || !data ? (
        <div className="h-48 animate-pulse rounded-xl bg-muted" />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2">
              {data.status === 'closed' ? <Badge variant="active">Final</Badge> : <Badge variant="pending">Provisional</Badge>}
              <span className="text-muted-foreground">
                Statement date {fmtDate(data.invoiceDate)} · Payment due {fmtDate(data.dueDate)}
              </span>
            </div>
            <div className="flex gap-3">
              <a className="inline-flex items-center gap-1 font-medium text-ata-light-blue hover:underline" href={`${base}.pdf`}>
                <Download className="size-3.5" /> PDF
              </a>
              <a className="inline-flex items-center gap-1 font-medium text-ata-light-blue hover:underline" href={`${base}.csv`}>
                <Download className="size-3.5" /> CSV
              </a>
            </div>
          </div>
          <Table>
            <THead>
              <Th>Brand</Th>
              <Th right>Licence Fees</Th>
              <Th right>Expansion Charges</Th>
              <Th right>Adjustments</Th>
              <Th right>Total (USD)</Th>
            </THead>
            <tbody>
              {data.brands.map((s) => (
                <Tr key={s.partner.id}>
                  <Td className="font-medium">
                    <Link to={`/p/${s.partner.slug}/statements`} className="hover:underline">
                      {s.partner.name}
                    </Link>
                    {s.status !== 'closed' && (
                      <span className="ml-2">
                        <Badge variant="pending">Not closed</Badge>
                      </span>
                    )}
                  </Td>
                  <Td right>{fmtMoney(s.totals.licenceFeesUsdMinor + s.totals.topUpsUsdMinor, 'USD')}</Td>
                  <Td right>{fmtMoney(s.totals.expansionUsdMinor, 'USD')}</Td>
                  <Td right>{fmtMoney(s.totals.adjustmentsUsdMinor, 'USD')}</Td>
                  <Td right>{fmtMoney(s.totals.totalUsdMinor, 'USD')}</Td>
                </Tr>
              ))}
              <TotalsRow>
                <Td>Total due</Td>
                <Td right />
                <Td right />
                <Td right />
                <Td right>{fmtMoney(data.totalUsdMinor, 'USD')}</Td>
              </TotalsRow>
            </tbody>
          </Table>
        </div>
      )}
    </section>
  )
}

/** How Atarim invoices the group. Atarim admins edit it; group users see it. */
function GroupBillingPanel({ group }: { group: GroupSummaryData['group'] }) {
  const { data: me } = useMeQuery()
  const isAdmin = me?.role === 'atarim_admin'
  const [save, { isLoading }] = useSetGroupBillingMutation()
  const [mode, setMode] = useState<GroupBillingMode>(group.billingMode)
  const [b, setB] = useState(group.billing)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setMode(group.billingMode)
    setB(group.billing)
  }, [group])
  const field = (key: 'entity' | 'address' | 'country' | 'vatNumber') => ({
    value: b[key],
    disabled: !isAdmin,
    onChange: (ev: React.ChangeEvent<HTMLInputElement>) => setB({ ...b, [key]: ev.target.value }),
  })

  if (!isAdmin && group.billingMode === 'per_brand') return null

  return (
    <section className="mt-8 max-w-3xl rounded-xl border border-border bg-card p-5 shadow-sm">
      <h3 className="text-base font-semibold">Group invoicing</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {isAdmin
          ? 'Invoice each brand separately, or once to the group. Brands still keep their own Entitlements, prices and statements.'
          : 'Printed on the group statement.'}
      </p>
      <form
        className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
        onSubmit={async (ev) => {
          ev.preventDefault()
          setError(null)
          try {
            await save({ id: group.id, billingMode: mode, billing: b }).unwrap()
            toast.success('Group invoicing saved')
          } catch (err) {
            setError(apiError(err))
          }
        }}
      >
        {isAdmin && (
          <div className="sm:col-span-2">
            <Label>Invoiced</Label>
            <Select
              ariaLabel="Invoiced"
              value={mode}
              onValueChange={(v) => setMode(v as GroupBillingMode)}
              options={[
                { value: 'per_brand', label: 'Per brand' },
                { value: 'group', label: 'Once, to the group' },
              ]}
            />
          </div>
        )}
        {mode === 'group' && (
          <>
            <div>
              <Label htmlFor="g-entity">Billing entity</Label>
              <Input id="g-entity" {...field('entity')} />
            </div>
            <div>
              <Label htmlFor="g-country">Country</Label>
              <Input id="g-country" {...field('country')} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="g-address">Address</Label>
              <Input id="g-address" {...field('address')} />
            </div>
            <div>
              <Label htmlFor="g-vat">VAT number</Label>
              <Input id="g-vat" {...field('vatNumber')} />
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <Switch
                checked={b.reverseCharge}
                disabled={!isAdmin}
                onCheckedChange={(v) => setB({ ...b, reverseCharge: v })}
                aria-label="Reverse charge"
              />
              Reverse charge applies
            </label>
          </>
        )}
        {isAdmin && (
          <div className="sm:col-span-2">
            <FieldError>{error}</FieldError>
            <Button type="submit" loading={isLoading}>
              Save group invoicing
            </Button>
          </div>
        )}
      </form>
    </section>
  )
}
